// src/components/announcements/AnnouncementBoard.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import AnnouncementCard from './AnnouncementCard';
import CreateAnnouncement from './CreateAnnouncement';
import '../../pages/UserDashboard.css';
import './AnnouncementBoard.css';

function AnnouncementBoard({ userData, onBack }) {
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  const categories = [
    { value: 'general', label: 'General', icon: '📌' },
    { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { value: 'event', label: 'Event', icon: '🎉' },
    { value: 'emergency', label: 'Emergency', icon: '🚨' },
    { value: 'financial', label: 'Financial', icon: '💰' },
    { value: 'rules', label: 'Rules', icon: '📋' }
  ];

  useEffect(() => {
    fetchAnnouncements();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
    
    // Set up real-time subscription
    const channel = supabase
      .channel('announcements-channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'announcements' },
        (payload) => {
          console.log('Real-time update:', payload);
          fetchAnnouncements(); // Refresh when any announcement changes
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [darkMode]);

  useEffect(() => {
    filterAnnouncements();
  }, [announcements, filter, searchTerm]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString();
      
      // Modified query to get all active announcements
      let { data, error } = await supabase
        .from('announcements')
        .select(`
          *,
          author:residents!announcements_author_id_fkey(name, flat_number)
        `)
        .eq('is_active', true)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching announcements:', error);
        throw error;
      }

      // Filter out expired announcements if needed
      const activeAnnouncements = data?.filter(announcement => {
        if (!announcement.expiry_date) return true;
        return new Date(announcement.expiry_date) >= new Date();
      }) || [];

      // Check read status for current user
      if (activeAnnouncements.length > 0 && userData?.id) {
        const { data: readData } = await supabase
          .from('announcement_reads')
          .select('announcement_id')
          .eq('resident_id', userData.id);

        const readIds = readData?.map(r => r.announcement_id) || [];
        
        const processedData = activeAnnouncements.map(announcement => ({
          ...announcement,
          isRead: readIds.includes(announcement.id)
        }));

        setAnnouncements(processedData);
      } else {
        setAnnouncements(activeAnnouncements);
      }

      console.log('Fetched announcements:', activeAnnouncements);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAnnouncements = () => {
    let filtered = [...announcements];

    if (filter !== 'all') {
      filtered = filtered.filter(a => a.category === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAnnouncements(filtered);
  };

  const markAsRead = async (announcementId) => {
    if (!userData?.id) return;
    
    try {
      await supabase
        .from('announcement_reads')
        .upsert({
          announcement_id: announcementId,
          resident_id: userData.id,
          read_at: new Date().toISOString()
        }, {
          onConflict: 'announcement_id,resident_id'
        });

      setAnnouncements(prev => prev.map(a => 
        a.id === announcementId ? {...a, isRead: true} : a
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_active: false })
        .eq('id', announcementId);

      if (error) throw error;

      // Remove from local state immediately
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      alert('Announcement deleted successfully');
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement');
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Refresh announcements after creation
    setTimeout(() => {
      fetchAnnouncements();
    }, 500);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const getStats = () => {
    return {
      total: announcements.length,
      unread: announcements.filter(a => !a.isRead).length,
      pinned: announcements.filter(a => a.is_pinned).length,
      urgent: announcements.filter(a => a.priority === 'urgent').length
    };
  };

  const stats = getStats();

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className="menu-item active"
            onClick={() => {
              fetchAnnouncements();
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📢</span>
            <span className="menu-label">All Announcements</span>
          </button>
          
          {isAdmin && (
            <button
              className="menu-item"
              onClick={() => {
                setShowCreateModal(true);
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">➕</span>
              <span className="menu-label">New Announcement</span>
            </button>
          )}
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Category</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="sidebar-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📢 Announcements</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="announcement-header">
            <h2 className="page-title">📢 Announcement Board</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={fetchAnnouncements}
                className="btn-secondary"
                title="Refresh"
              >
                🔄
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  + New Announcement
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📊 Total</h3>
                <p className="stat-value">{stats.total}</p>
                <small>Announcements</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-pink">
              <div className="stat-content">
                <h3>🔔 Unread</h3>
                <p className="stat-value">{stats.unread}</p>
                <small>New Messages</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-blue">
              <div className="stat-content">
                <h3>📌 Pinned</h3>
                <p className="stat-value">{stats.pinned}</p>
                <small>Important</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-red">
              <div className="stat-content">
                <h3>🚨 Urgent</h3>
                <p className="stat-value">{stats.urgent}</p>
                <small>Priority</small>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="dashboard-card search-card">
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Announcements List */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading announcements...</p>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="dashboard-card empty-state">
              <div className="empty-icon">📭</div>
              <p>No announcements found</p>
              <small>Check back later for updates</small>
            </div>
          ) : (
            <div className="announcements-list">
              {filteredAnnouncements.map(announcement => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  userData={userData}
                  onMarkAsRead={markAsRead}
                  onDelete={handleDelete}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAnnouncement
          userData={userData}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}

export default AnnouncementBoard;